import { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { FileText, Download } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Reports({ user, onLogout }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/reports/summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReports(response.data);
    } catch (error) {
      toast.error('Failed to fetch reports');
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (reports.length === 0) {
      toast.error('No data to export');
      return;
    }

    // Create CSV headers
    const headers = [
      'Employee Name',
      'Email',
      'Paid Leaves Taken',
      'Unpaid Leaves',
      'Balance Leaves',
      'Comp Off Earned',
      'Leave Breakdown'
    ];

    // Create CSV rows
    const rows = reports.map(report => {
      const breakdownStr = Object.entries(report.leave_breakdown || {})
        .map(([type, days]) => `${type}: ${days}`)
        .join('; ');
      
      return [
        report.user_name,
        report.user_email,
        report.total_paid_leaves,
        report.total_unpaid_leaves,
        report.balance_leaves || 0,
        report.total_comp_off,
        breakdownStr || 'N/A'
      ];
    });

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `leave_report_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
    
    toast.success('Report exported successfully!');
  };

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar user={user} onLogout={onLogout} />
      
      <div className="flex-1 md:ml-0">
        <div className="bg-white border-b border-border px-4 md:px-8 py-4 md:py-6 pt-16 md:pt-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 md:w-6 md:h-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">Leave Summary Reports</h1>
                <p className="text-sm md:text-base text-muted-foreground mt-1">Employee-wise leave breakdown</p>
              </div>
            </div>
            <button 
              onClick={exportToCSV}
              data-testid="export-report-button"
              className="flex items-center justify-center gap-2 px-4 py-2 md:py-2.5 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors shadow-sm text-sm md:text-base w-full md:w-auto"
            >
              <Download className="w-4 h-4" />
              Export Report
            </button>
          </div>
        </div>

        <div className="p-4 md:p-8">
          {loading ? (
            <div className="bg-white rounded-xl border border-border shadow-sm p-12 text-center">
              <p className="text-muted-foreground">Loading reports...</p>
            </div>
          ) : reports.length === 0 ? (
            <div className="bg-white rounded-xl border border-border shadow-sm p-12 text-center">
              <p className="text-muted-foreground">No reports available</p>
            </div>
          ) : (
            <div className="space-y-4 md:space-y-6">
              {reports.map((report) => (
                <div key={report.user_email} className="bg-white rounded-xl border border-border shadow-sm p-4 md:p-8" data-testid={`report-${report.user_email}`}>
                  {/* Mobile: Stack layout, Desktop: Side by side */}
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-4 md:mb-6">
                    {/* Employee Info */}
                    <div className="flex items-center gap-3 mb-4 md:mb-0">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-primary font-semibold text-sm md:text-base">
                          {report.user_name.split(' ').map(n => n[0]).join('').toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-heading font-semibold text-base md:text-xl text-foreground truncate">{report.user_name}</h3>
                        <p className="text-xs md:text-sm text-muted-foreground truncate">{report.user_email}</p>
                      </div>
                    </div>
                    
                    {/* Stats - Grid on mobile, row on desktop */}
                    <div className="grid grid-cols-4 gap-2 md:gap-6">
                      <div className="text-center bg-muted/50 md:bg-transparent rounded-lg p-2 md:p-0">
                        <p className="text-[10px] md:text-xs font-medium text-muted-foreground uppercase tracking-wider">Paid</p>
                        <p className="text-lg md:text-2xl font-bold font-heading text-foreground" data-testid={`paid-${report.user_email}`}>
                          {report.total_paid_leaves}
                        </p>
                      </div>
                      <div className="text-center bg-muted/50 md:bg-transparent rounded-lg p-2 md:p-0">
                        <p className="text-[10px] md:text-xs font-medium text-muted-foreground uppercase tracking-wider">Unpaid</p>
                        <p className="text-lg md:text-2xl font-bold font-heading text-foreground" data-testid={`unpaid-${report.user_email}`}>
                          {report.total_unpaid_leaves}
                        </p>
                      </div>
                      <div className="text-center bg-muted/50 md:bg-transparent rounded-lg p-2 md:p-0">
                        <p className="text-[10px] md:text-xs font-medium text-muted-foreground uppercase tracking-wider">Balance</p>
                        <p className="text-lg md:text-2xl font-bold font-heading text-green-600" data-testid={`balance-${report.user_email}`}>
                          {report.balance_leaves || 0}
                        </p>
                      </div>
                      <div className="text-center bg-muted/50 md:bg-transparent rounded-lg p-2 md:p-0">
                        <p className="text-[10px] md:text-xs font-medium text-muted-foreground uppercase tracking-wider">Comp</p>
                        <p className="text-lg md:text-2xl font-bold font-heading text-primary" data-testid={`comp-${report.user_email}`}>
                          {report.total_comp_off}
                        </p>
                      </div>
                    </div>
                  </div>

                  {Object.keys(report.leave_breakdown || {}).length > 0 && (
                    <div className="border-t border-border pt-4 md:pt-6">
                      <h4 className="font-heading font-semibold text-xs md:text-sm text-foreground mb-3 md:mb-4">Leave Type Breakdown</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
                        {Object.entries(report.leave_breakdown).map(([type, days]) => (
                          <div key={type} className="bg-muted rounded-lg md:rounded-xl p-3 md:p-4" data-testid={`breakdown-${report.user_email}-${type}`}>
                            <p className="text-[10px] md:text-xs text-muted-foreground">{type}</p>
                            <p className="mt-1 text-sm md:text-lg font-semibold font-heading text-foreground">{days} days</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
